import type { UserRole } from '../types/auth'

export type Capability =
  | 'adminLike'
  | 'manageOrganizations'
  | 'manageOrgStructure'
  | 'manageUsers'
  | 'manageTemplates'
  | 'manageReferences'
  | 'createTasks'
  | 'viewOrgTasks'
  | 'viewAssignedTasks'
  | 'createReports'
  | 'viewOrgReports'
  | 'viewOwnReports'
  | 'useAIAssistant'
  | 'manageFields'
  | 'viewFields'
  | 'manageAccessGrants'
  | 'viewGrantedFarms'
  | 'manageConsultations'
  | 'manageTreatments'
  | 'completeTreatments'
  | 'viewActionLog'
  | 'manageFieldTrials'
  | 'viewFieldTrials'

const normalizeRole = (role: UserRole): UserRole => {
  switch (role) {
    case 'admin':
      return 'org_admin'
    default:
      return role
  }
}

const capabilityMap: Record<UserRole, Capability[]> = {
  super_admin: [
    'adminLike',
    'manageOrganizations',
    'manageOrgStructure',
    'manageUsers',
    'manageTemplates',
    'manageReferences',
    'createTasks',
    'viewOrgTasks',
    'viewAssignedTasks',
    'createReports',
    'viewOrgReports',
    'viewOwnReports',
    'useAIAssistant',
    'manageFields',
    'viewFields',
    'manageAccessGrants',
    'viewGrantedFarms',
    'manageConsultations',
    'manageTreatments',
    'completeTreatments',
    'viewActionLog',
    'manageFieldTrials',
    'viewFieldTrials',
  ],
  org_admin: [
    'adminLike',
    'manageOrgStructure',
    'manageUsers',
    'manageTemplates',
    'manageReferences',
    'createTasks',
    'viewOrgTasks',
    'createReports',
    'viewOrgReports',
    'viewOwnReports',
    'useAIAssistant',
    'manageFields',
    'viewFields',
    'manageAccessGrants',
    'manageConsultations',
    'manageTreatments',
    'completeTreatments',
    'viewActionLog',
    'viewFieldTrials',
  ],
  agronomist: [
    'manageTemplates',
    'manageReferences',
    'createTasks',
    'viewAssignedTasks',
    'createReports',
    'viewOrgReports',
    'viewOwnReports',
    'useAIAssistant',
    'manageFields',
    'viewFields',
    'manageAccessGrants',
    'manageConsultations',
    'manageTreatments',
    'completeTreatments',
    'viewFieldTrials',
  ],
  consultant: [
    'viewAssignedTasks',
    'createReports',
    'viewOwnReports',
    'useAIAssistant',
    'viewFields',
  ],
  field_worker: [
    'viewAssignedTasks',
    'createReports',
    'viewOwnReports',
    'useAIAssistant',
    'viewFields',
    'completeTreatments',
  ],
  company_admin: [
    'viewGrantedFarms',
    'useAIAssistant',
    'viewOwnReports',
    'manageConsultations',
    'manageUsers',
    'viewActionLog',
    'manageFieldTrials',
    'viewFieldTrials',
  ],
  regional_manager: [
    'viewGrantedFarms',
    'useAIAssistant',
    'viewOwnReports',
    'manageConsultations',
    'manageFieldTrials',
    'viewFieldTrials',
  ],
  manager: ['viewGrantedFarms', 'useAIAssistant', 'viewOwnReports', 'viewFieldTrials'],
  agronomist_consultant: [
    'viewGrantedFarms',
    'useAIAssistant',
    'viewOwnReports',
    'manageConsultations',
    'createReports',
    'viewFieldTrials',
  ],
  field_trials_lead: [
    'viewGrantedFarms',
    'useAIAssistant',
    'viewOwnReports',
    'createReports',
    'manageFieldTrials',
    'viewFieldTrials',
  ],
  analyst: ['viewGrantedFarms', 'useAIAssistant', 'viewOwnReports', 'viewFieldTrials'],
  observer: ['viewGrantedFarms', 'useAIAssistant', 'viewOwnReports', 'viewFieldTrials'],
  admin: [
    'adminLike',
    'manageOrgStructure',
    'manageUsers',
    'manageTemplates',
    'manageReferences',
    'createTasks',
    'viewOrgTasks',
    'createReports',
    'viewOrgReports',
    'viewOwnReports',
    'useAIAssistant',
    'manageFields',
    'viewFields',
    'manageAccessGrants',
    'manageConsultations',
    'manageTreatments',
    'completeTreatments',
  ],
  viewer: ['viewOwnReports', 'useAIAssistant', 'viewFields'],
}

export const hasCapability = (role: UserRole, capability: Capability): boolean => {
  const normalizedRole = normalizeRole(role)
  return capabilityMap[normalizedRole].includes(capability)
}

export const hasAnyCapability = (role: UserRole, capabilities: Capability[]): boolean =>
  capabilities.some(capability => hasCapability(role, capability))

export const isRoleAllowed = (role: UserRole, allowedRoles: UserRole[]): boolean => {
  const normalizedRole = normalizeRole(role)
  return allowedRoles.some(allowedRole => normalizeRole(allowedRole) === normalizedRole)
}
